import React, { useState } from "react";
import { Form, Button, Card, Container, Alert } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { serverUrl } from "../../serverUrl";
import { toast } from "react-toastify";
import { FaLock, FaCheckCircle } from "react-icons/fa";

function ResetPassword() {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: ""
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validated, setValidated] = useState(false);

    // Get params from path: /reset-password/:uidb64/:token/:token_valid
    const { uidb64, token, token_valid } = useParams();

    // Validate token_valid param (should be "True" or "False")
    const tokenValid = token_valid === "True";

    // Ensure all params are present
    const paramsMissing = !uidb64 || !token || typeof token_valid === "undefined";

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() && formData.password === formData.confirmPassword) {
            setIsSubmitting(true);

            serverUrl.patch("/users/password-reset-complete", {
                uidb64,
                token,
                password: formData.confirmPassword,
            })
                .then(() => {
                    toast.success("Password reset successfully");
                    navigate("/login");
                })
                .catch(error => {
                    toast.error(error.response?.data?.message || "Failed to reset password");
                })
                .finally(() => setIsSubmitting(false));
        }

        setValidated(true);
    };

    if (paramsMissing) {
        return (
            <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                <Card className="shadow-sm w-100" style={{ maxWidth: "500px" }}>
                    <Card.Body className="p-4 text-center">
                        <Alert variant="danger" className="mb-4">
                            <h5 className="alert-heading">Invalid Link</h5>
                            <p>Required parameters are missing from the reset link.</p>
                        </Alert>
                        <Button variant="outline-primary" onClick={() => navigate("/forgot-password")}>
                            Request New Reset Link
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!tokenValid) {
        return (
            <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
                <Card className="shadow-sm w-100" style={{ maxWidth: "500px" }}>
                    <Card.Body className="p-4 text-center">
                        <Alert variant="danger" className="mb-4">
                            <h5 className="alert-heading">Invalid Token</h5>
                            <p>The password reset link is invalid or has expired.</p>
                        </Alert>
                        <Button variant="outline-primary" onClick={() => navigate("/forgot-password")}>
                            Request New Reset Link
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
            <Card className="shadow w-100" style={{ maxWidth: "500px" }}>
                <Card.Body className="p-4">
                    <div className="text-center mb-4">
                        <FaCheckCircle className="text-success text-center mb-3" size={48} />
                        <h3>Reset Your Password</h3>
                        <p className="text-muted">Please enter your new password below</p>
                    </div>

                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <p className="text-start">New Password</p>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FaLock />
                                </span>
                                <Form.Control
                                    required
                                    type="password"
                                    placeholder="Enter new password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    minLength={8}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Password must be at least 8 characters
                                </Form.Control.Feedback>
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <p className="text-start">Confirm Password</p>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FaLock />
                                </span>
                                <Form.Control
                                    required
                                    type="password"
                                    placeholder="Confirm new password"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    isInvalid={formData.password !== formData.confirmPassword}
                                />
                                <Form.Control.Feedback type="invalid">
                                    Passwords do not match
                                </Form.Control.Feedback>
                            </div>
                        </Form.Group>

                        <Button
                            variant="primary"
                            type="submit"
                            className="w-100 py-2"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? "Resetting..." : "Reset Password"}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default ResetPassword;